// Drug interactions modal (MEDICATION_INTERACTIONS_IMPLEMENTATION.md Phase 3 §3.2).
// Shared between the Medications page (comprehensive review) and the Chat pane
// (real-time warning, filtered by patient alert level settings).
//
// Props:
//   drugNames?        – if absent, the backend does a comprehensive review (all
//                       regimen meds). If present, treats as ad-hoc list.
//   focusNames?       – restrict warnings to pairs touching one of these (the
//                       meds being marked taken in chat). Keeps the modal in step
//                       with the inline chat warnings.
//   patientId         – required for all API calls
//   patientName       – shown in the sticky header
//   filterBySeverity? – when set, only these severity groups are displayed and a
//                       note explains the others are hidden by user settings
//   showHiddenOnly?   – when true, show only levels NOT in filterBySeverity
//   showSeverityFilters? – comprehensive review only: render in-view, ephemeral
//                       per-severity toggle switches at the bottom so the user
//                       can reveal/hide categories without changing their saved
//                       Preferences. Defaults the toggles to filterBySeverity.
//   onClose           – dismiss callback

import { useEffect, useState } from "react";
import {
  INTERACTION_SEVERITIES,
  resolveInteractionAlertLevels,
  type InteractionSeverity,
  type InteractionWarning,
} from "@medassist/shared";
import { checkDrugInteractions, type InteractionCheckResponse, ApiError } from "../lib/api.js";
import { Toggle } from "./Toggle.js";
import { ModalShell } from "./ModalShell.js";

// ── Severity metadata ──────────────────────────────────────────────────────────

interface SeverityMeta {
  label: string;
  badgeCls: string;    // badge background + text
  groupCls: string;    // accordion header border + tint
  textCls: string;     // description text colour
  defaultOpen: boolean;
}

const SEVERITY_META: Record<InteractionSeverity, SeverityMeta> = {
  contraindicated: {
    label: "Contraindicated",
    badgeCls: "bg-red-600 text-white",
    groupCls: "border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40",
    textCls: "text-red-800 dark:text-red-300",
    defaultOpen: true,
  },
  do_not_use: {
    label: "Do Not Use Together",
    badgeCls: "bg-orange-600 text-white",
    groupCls: "border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/40",
    textCls: "text-orange-800 dark:text-orange-300",
    defaultOpen: true,
  },
  serious: {
    label: "Serious",
    badgeCls: "bg-amber-500 text-white",
    groupCls: "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40",
    textCls: "text-amber-800 dark:text-amber-300",
    defaultOpen: true,
  },
  moderate: {
    label: "Moderate",
    badgeCls: "bg-yellow-400 text-yellow-900",
    groupCls: "border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-950/40",
    textCls: "text-yellow-800 dark:text-yellow-300",
    defaultOpen: true,
  },
  minor: {
    label: "Minor",
    badgeCls: "bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-200",
    groupCls: "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30",
    textCls: "text-gray-600 dark:text-gray-400",
    defaultOpen: false,
  },
};

// ── Accordion group ────────────────────────────────────────────────────────────

function SeverityGroup({
  severity,
  warnings,
  open,
  onToggle,
}: {
  severity: InteractionSeverity;
  warnings: InteractionWarning[];
  open: boolean;
  onToggle: () => void;
}) {
  const meta = SEVERITY_META[severity];

  return (
    <div className={`mb-2 rounded-lg border ${meta.groupCls}`}>
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
        aria-expanded={open}
      >
        <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${meta.badgeCls}`}>
          {meta.label}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {warnings.length} interaction{warnings.length !== 1 ? "s" : ""}
        </span>
        <span className="ml-auto text-xs text-gray-400">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700 px-3 pb-2">
          {warnings.map((w, i) => (
            <li key={i} className="py-2">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {w.drug1} + {w.drug2}
              </p>
              {w.description && (
                <p className={`mt-0.5 text-xs ${meta.textCls}`}>{w.description}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────────

export function DrugInteractionsModal({
  drugNames,
  focusNames,
  patientId,
  patientName,
  filterBySeverity,
  showHiddenOnly = false,
  showSeverityFilters = false,
  onClose,
}: {
  drugNames?: string[];
  focusNames?: string[];
  patientId: string;
  patientName: string;
  filterBySeverity?: InteractionSeverity[];
  showHiddenOnly?: boolean;
  showSeverityFilters?: boolean;
  onClose: () => void;
}) {
  const [result, setResult] = useState<InteractionCheckResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-severity accordion open state, lifted so an "Expand all" toggle can drive
  // every group at once. Seeded from each severity's default-open preference.
  const [openSeverities, setOpenSeverities] = useState<Set<InteractionSeverity>>(
    () => new Set(INTERACTION_SEVERITIES.filter((s) => SEVERITY_META[s].defaultOpen))
  );
  function toggleSeverityOpen(sev: InteractionSeverity) {
    setOpenSeverities((prev) => {
      const next = new Set(prev);
      if (next.has(sev)) next.delete(sev);
      else next.add(sev);
      return next;
    });
  }

  // Effective alert levels: when filterBySeverity is absent we show all severities.
  const alertLevels = filterBySeverity
    ? resolveInteractionAlertLevels(filterBySeverity)
    : ([...INTERACTION_SEVERITIES] as InteractionSeverity[]);

  // In-view, ephemeral severity visibility (comprehensive review only). Seeded
  // from the patient's effective alert levels; toggling never touches saved
  // Preferences and is not persisted anywhere.
  const [shownSeverities, setShownSeverities] = useState<Set<InteractionSeverity>>(
    () => new Set(alertLevels)
  );
  function toggleShown(sev: InteractionSeverity) {
    setShownSeverities((prev) => {
      const next = new Set(prev);
      if (next.has(sev)) next.delete(sev);
      else next.add(sev);
      return next;
    });
  }

  useEffect(() => {
    void doCheck(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  async function doCheck(force: boolean) {
    setLoading(true);
    setError(null);
    try {
      const res = await checkDrugInteractions(
        patientId,
        drugNames,
        force || undefined,
        focusNames?.length ? focusNames : undefined
      );
      setResult(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to check interactions");
    } finally {
      setLoading(false);
    }
  }

  // Decide which warnings to show. In comprehensive-review mode the in-view
  // toggles drive visibility; otherwise fall back to the prop-based logic.
  const visibleWarnings: InteractionWarning[] = result
    ? result.warnings.filter((w) => {
        if (showSeverityFilters) return shownSeverities.has(w.severity);
        if (showHiddenOnly && filterBySeverity) {
          return !filterBySeverity.includes(w.severity);
        }
        return alertLevels.includes(w.severity);
      })
    : [];

  // Per-severity counts across the full result (drives the toggle row labels).
  const countBySeverity: Record<InteractionSeverity, number> = {
    contraindicated: 0,
    do_not_use: 0,
    serious: 0,
    moderate: 0,
    minor: 0,
  };
  if (result) for (const w of result.warnings) countBySeverity[w.severity]++;

  // Group visible warnings by severity (ordered).
  const bySeverity: Partial<Record<InteractionSeverity, InteractionWarning[]>> = {};
  for (const sev of INTERACTION_SEVERITIES) {
    const group = visibleWarnings.filter((w) => w.severity === sev);
    if (group.length > 0) bySeverity[sev] = group;
  }

  const hasAny = visibleWarnings.length > 0;
  const visibleSeverities = INTERACTION_SEVERITIES.filter((sev) => bySeverity[sev]);
  const allExpanded =
    visibleSeverities.length > 0 && visibleSeverities.every((sev) => openSeverities.has(sev));

  function toggleExpandAll() {
    setOpenSeverities((prev) => {
      if (allExpanded) {
        const next = new Set(prev);
        for (const sev of visibleSeverities) next.delete(sev);
        return next;
      }
      const next = new Set(prev);
      for (const sev of visibleSeverities) next.add(sev);
      return next;
    });
  }
  const hiddenCount =
    result && filterBySeverity
      ? result.warnings.filter((w) => !filterBySeverity.includes(w.severity)).length
      : 0;

  const headerActions = (
    <button
      onClick={() => void doCheck(true)}
      disabled={loading}
      className="shrink-0 rounded-md bg-(--accent) px-3 py-1.5 text-sm text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
    >
      {loading ? "Checking…" : "Re-check"}
    </button>
  );

  const footer = (
    <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
      <strong>Disclaimer:</strong> Interaction data is sourced from the NLM RxNav database and
      is provided for informational purposes only. It is not a substitute for professional
      medical advice, diagnosis, or treatment. Always consult your pharmacist or prescribing
      clinician before making medication changes.
    </p>
  );

  return (
    <ModalShell
      title={`Drug Interactions — ${patientName}`}
      subtitle={
        result?.checkedAt
          ? `Last checked ${new Date(result.checkedAt).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}${result.fromCache ? " (cached)" : ""}`
          : undefined
      }
      headerActions={headerActions}
      footer={footer}
      size="2xl"
      onClose={onClose}
    >
      {loading && (
            <p className="mt-6 text-center text-sm text-gray-400">Checking interactions…</p>
          )}

          {!loading && error && (
            <div className="rounded-md bg-red-50 dark:bg-red-950 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {!loading && !error && result && (
            <>
              {/* Interaction check could not complete — never show this as "none found". */}
              {result.interactionsUnavailable && (
                <p className="mb-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                  Interaction checking is temporarily unavailable — results could not be
                  retrieved. Try “Re-check” in a moment. This is <strong>not</strong> a
                  confirmation that these medications are safe to combine.
                </p>
              )}

              {/* RxNav-unreachable / AI-fallback note — low-key, per spec §6 */}
              {(result.rxnavUnreachable || result.fallbackUsed) && (
                <p className="mb-3 rounded-md bg-gray-100 dark:bg-gray-800 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                  Some names were resolved via an AI fallback because the drug database was briefly
                  unreachable.
                </p>
              )}

              {/* Unresolved drugs */}
              {result.unresolved.length > 0 && (
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                  Could not identify:{" "}
                  <span className="italic">{result.unresolved.join(", ")}</span>
                </p>
              )}

              {/* Filter note when in "filtered" mode (suppressed when in-view
                  toggles are present — those communicate the same thing). */}
              {filterBySeverity && !showHiddenOnly && !showSeverityFilters && (
                <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                  Showing interactions at your configured alert levels
                  {hiddenCount > 0 ? ` (${hiddenCount} below your threshold — adjust in Preferences)` : ""}.
                </p>
              )}

              {showHiddenOnly && (
                <p className="mb-3 rounded-md bg-gray-100 dark:bg-gray-800 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                  These interactions are below your configured alert level. Adjust levels in
                  Preferences to enable alerts.
                </p>
              )}

              {/* Severity accordion groups */}
              {hasAny ? (
                <>
                  {visibleSeverities.length > 1 && (
                    <div className="mb-2 flex justify-end">
                      <button
                        onClick={toggleExpandAll}
                        className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                      >
                        {allExpanded ? "Collapse all" : "Expand all"}
                      </button>
                    </div>
                  )}
                  {INTERACTION_SEVERITIES.map((sev) => {
                    const group = bySeverity[sev];
                    if (!group) return null;
                    return (
                      <SeverityGroup
                        key={sev}
                        severity={sev}
                        warnings={group}
                        open={openSeverities.has(sev)}
                        onToggle={() => toggleSeverityOpen(sev)}
                      />
                    );
                  })}
                </>
              ) : (
                <p className="mt-6 text-center text-sm text-gray-400 dark:text-gray-500">
                  {result.interactionsUnavailable
                    ? "Interaction check could not be completed."
                    : result.warnings.length === 0
                      ? "No interactions found among these medications."
                      : showSeverityFilters
                        ? "No interactions in the categories shown below."
                        : "No interactions at your configured alert levels."}
                </p>
              )}

              {/* In-view severity toggles — ephemeral, do not change Preferences. */}
              {showSeverityFilters && !result.interactionsUnavailable && (
                <div className="mt-5 rounded-lg border border-gray-200 dark:border-gray-800 p-3">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Show categories
                  </p>
                  <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">
                    Reveal or hide interaction categories in this view only. This doesn't change your
                    saved alert settings — adjust those in Preferences.
                  </p>
                  <ul className="space-y-2">
                    {INTERACTION_SEVERITIES.map((sev) => {
                      const meta = SEVERITY_META[sev];
                      const count = countBySeverity[sev];
                      return (
                        <li key={sev} className="flex items-center gap-3">
                          <Toggle
                            id={`show-sev-${sev}`}
                            checked={shownSeverities.has(sev)}
                            onChange={() => toggleShown(sev)}
                          />
                          <label
                            htmlFor={`show-sev-${sev}`}
                            className="flex flex-1 items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                          >
                            <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${meta.badgeCls}`}>
                              {meta.label}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {count} interaction{count !== 1 ? "s" : ""}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
    </ModalShell>
  );
}
